import { NgIf } from '@angular/common';
import { Component, ElementRef, Inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import {
    MatProgressSpinner,
    MatProgressSpinnerModule,
} from '@angular/material/progress-spinner';
import { ExportAsModule } from 'ngx-export-as';
import { SharedModule } from '../shared/shared.module';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
declare const require: any;

@Component({
    selector: 'app-food-handling-services',
    imports: [
        SharedModule,
        MatButton,
        ExportAsModule,
        MatProgressSpinner,
        MatProgressSpinnerModule,
        NgIf,
    ],
    templateUrl: './food-handling-services.component.html',
    styleUrl: './food-handling-services.component.scss',
})
export class FoodHandlingServicesComponent {
    isExporting = false;

    constructor(
        public dialogRef: MatDialogRef<FoodHandlingServicesComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            contractName: string;
            icNumber: string;
            address: string;
            signature: string;
            currentDate: string;
            // Regenerate Contract only; registration leaves these out and the template keeps its defaults
            startDate?: string;
            endDateText?: string;
            endCode?: string;
            positionApplied: string;
        },
        private elementRef: ElementRef
    ) {}

    onClose(): void {
        this.dialogRef.close();
    }

    private async generatePdf(): Promise<jsPDF> {
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: true,
        });

        const A4_WIDTH_PX = 794;
        const sections = document.querySelectorAll('.break-before-page');
        console.log(`Found ${sections.length} sections to process`);

        for (let i = 0; i < sections.length; i++) {
            const section = sections[i] as HTMLElement;
            console.log(`Processing section ${i + 1}`);

            // Force fixed width for consistent rendering across all screen sizes
            const originalWidth = section.style.width;
            const originalMaxWidth = section.style.maxWidth;
            const originalMinWidth = section.style.minWidth;
            section.style.width = `${A4_WIDTH_PX}px`;
            section.style.maxWidth = `${A4_WIDTH_PX}px`;
            section.style.minWidth = `${A4_WIDTH_PX}px`;

            const canvas = await html2canvas(section, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                scrollY: -window.scrollY,
                width: A4_WIDTH_PX,
                height: section.scrollHeight,
            });

            // Restore original width
            section.style.width = originalWidth;
            section.style.maxWidth = originalMaxWidth;
            section.style.minWidth = originalMinWidth;

            const imgData = canvas.toDataURL('image/jpeg', 0.8);
            const imgWidth = 210;
            const pageHeight = 297;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;

            if (i > 0) {
                pdf.addPage();
            }

            pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            console.log(`Completed section ${i + 1}`);
        }

        return pdf;
    }

    async exportToPDF(): Promise<void> {
        this.isExporting = true;
        console.log('Starting PDF export...');

        try {
            const pdf = await this.generatePdf();
            pdf.save(`contract-${this.data.contractName}.pdf`);
            console.log('PDF generation completed');
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            this.isExporting = false;
        }
    }

    async onAccept(): Promise<void> {
        this.isExporting = true;
        console.log('Starting PDF export...');

        try {
            const pdf = await this.generatePdf();

            const timestamp = new Date().getTime();
            const fileName = `contract-${this.data.contractName}-${timestamp}.pdf`;
            const pdfBlob = pdf.output('blob');

            console.log(
                `PDF generated: ${(pdfBlob.size / 1024 / 1024).toFixed(2)} MB`
            );

            this.dialogRef.close({
                data: this.data,
                pdfBlob: pdfBlob,
                fileName: fileName,
            });
            console.log('PDF generation completed');
        } catch (error) {
            console.error('Error in contract generation:', error);
            alert('Error generating contract. Please try again.');
        } finally {
            this.isExporting = false;
        }
    }
}
